import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import Modal from '../../components/ui/Modal';
import FormInput from '../../components/forms/FormInput';
import { getStaff, getByType, getTodayRoster, getRosterByDate, filterStaff, createStaff, deleteStaff } from '../../api/staff';

const tabs = [
  { key: 'cleaning', label: 'Cleaning Staff' },
  { key: 'security', label: 'Security Staff' },
  { key: 'roster', label: 'Schedule Overview' },
];

const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const dayLabels = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };

const towerAFloors = ['1','2','3','4','5','6','7','8','9','10','11','12','13','14','15','16','17','18'];
const towerBFloors = Array.from({ length: 18 }, (_, i) => String(i + 1));
const securityPosts = ['Gate 1', 'Gate 2', 'Main Entrance', 'Common Area'];

const shiftOptions = [
  { value: '', label: 'All Shifts' },
  { value: 'day', label: 'Day Shift' },
  { value: 'evening', label: 'Evening Shift' },
  { value: 'night', label: 'Night Shift' },
];

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'on_leave', label: 'On Leave' },
  { value: 'absent', label: 'Absent' },
];
const parkingCleaningSections = ['Parking B1', 'Parking B2', 'Parking 1'];
const towerCleaningSections = ['Floor Mopping', 'Floor Sweeping', 'Garbage Removal'];
const generalCleaningSections = [
  'Lobby GF', 'Watering Plants', 'Conference Hall', 'Ladies Gym', 'Multi Gym', 'Common Toilets', 'Front Area', 'Back Side', 'Tracks', 'Games Room',
];

const formatTime12 = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
};

const getInitials = (name) => {
  if (!name) return '??';
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
};

const getAssignmentSummary = (member) => {
  if (!member.assignments || member.assignments.length === 0) return null;
  if (member.staff_type === 'security') {
    return { posts: member.assignments.map((a) => a.security_post).filter(Boolean) };
  }
  const towers = {};
  const sections = [];
  member.assignments.forEach((a) => {
    if (a.assignment_type === 'tower_floor' && a.tower) {
      if (!towers[a.tower]) towers[a.tower] = [];
      if (a.floor) towers[a.tower].push(a.floor);
    }
    if (a.assignment_type === 'cleaning_section' && a.cleaning_section) {
      sections.push(a.cleaning_section);
    }
  });
  return { towers, sections };
};

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cleaning');
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const [rosterFilters, setRosterFilters] = useState({
    service_type: '', date: today, status: '', tower: '', floor: '', shift: '',
  });
  const [form, setForm] = useState({
    name: '', phone: '', staff_type: 'cleaning',
    work_start_time: '09:00', work_end_time: '17:00',
    working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    towers: [], floorsA: [], floorsB: [], securityPosts: [], cleaningSections: [],
  });

  const fetchStaff = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      let res;
      if (activeTab === 'roster') {
        const hasFilters = rosterFilters.service_type || rosterFilters.status || rosterFilters.tower || rosterFilters.floor || rosterFilters.shift;
        if (hasFilters) {
          const filterPayload = {};
          if (rosterFilters.service_type) filterPayload.staff_type = rosterFilters.service_type;
          if (rosterFilters.status) filterPayload.status = rosterFilters.status;
          if (rosterFilters.tower) filterPayload.tower = rosterFilters.tower;
          if (rosterFilters.floor) filterPayload.floor = rosterFilters.floor;
          if (rosterFilters.shift) filterPayload.shift_type = rosterFilters.shift;
          res = await filterStaff(filterPayload);
        } else if (rosterFilters.date && rosterFilters.date !== today) {
          res = await getRosterByDate(rosterFilters.date);
        } else {
          res = await getTodayRoster();
        }
      } else {
        res = await getByType(activeTab);
      }
      const raw = res.data?.data || res.data || [];
      let data = Array.isArray(raw) ? raw : [];
      // Ensure only the correct staff type is shown for cleaning/security tabs
      if (activeTab === 'cleaning' || activeTab === 'security') {
        data = data.filter((s) => s.staff_type === activeTab);
      }
      setStaff(data);
      setMeta(res.data?.meta || { current_page: 1, last_page: 1 });
    } catch (err) {
      console.error('Failed to load staff', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, rosterFilters, today]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const toggleDay = (day) => {
    const days = form.working_days.includes(day)
      ? form.working_days.filter((d) => d !== day)
      : [...form.working_days, day];
    setForm({ ...form, working_days: days });
  };

  const toggleArrayItem = (field, item) => {
    const arr = form[field];
    const updated = arr.includes(item) ? arr.filter((v) => v !== item) : [...arr, item];
    setForm({ ...form, [field]: updated });
  };

  const resetForm = () => {
    setForm({
      name: '', phone: '', staff_type: 'cleaning',
      work_start_time: '09:00', work_end_time: '17:00',
      working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      towers: [], floorsA: [], floorsB: [], securityPosts: [], cleaningSections: [],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const assignments = [];
      if (form.staff_type === 'cleaning') {
        if (form.towers.includes('Tower A')) {
          form.floorsA.forEach((floor) => {
            assignments.push({ assignment_type: 'tower_floor', tower: 'Tower A', floor, security_post: null, cleaning_section: null });
          });
        }
        if (form.towers.includes('Tower B')) {
          form.floorsB.forEach((floor) => {
            assignments.push({ assignment_type: 'tower_floor', tower: 'Tower B', floor, security_post: null, cleaning_section: null });
          });
        }
        form.cleaningSections.forEach((section) => {
          assignments.push({ assignment_type: 'cleaning_section', tower: null, floor: null, security_post: null, cleaning_section: section });
        });
      } else {
        form.securityPosts.forEach((post) => {
          assignments.push({ assignment_type: 'security_post', tower: null, floor: null, security_post: post });
        });
      }

      await createStaff({
        name: form.name,
        phone: form.phone,
        staff_type: form.staff_type,
        shift_type: 'Day',
        work_start_time: form.work_start_time,
        work_end_time: form.work_end_time,
        working_days: form.working_days,
        assignments: assignments.length > 0 ? assignments : [{ assignment_type: form.staff_type === 'security' ? 'security_post' : 'tower_floor', tower: null, floor: null, security_post: null, cleaning_section: null }],
      });
      setShowModal(false);
      resetForm();
      fetchStaff();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create staff');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this staff member?')) return;
    try {
      await deleteStaff(id);
      fetchStaff(meta.current_page);
    } catch (err) {
      alert('Failed to delete staff');
    }
  };

  const handleFilterChange = (key, value) => {
    setRosterFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setRosterFilters({ service_type: '', date: today, status: '', tower: '', floor: '', shift: '' });
  };

  const getRosterFloorOptions = () => {
    switch (rosterFilters.tower) {
      case 'Tower A': return towerAFloors;
      case 'Tower B': return towerBFloors;
      default: return [];
    }
  };

  const tabTitle = activeTab === 'cleaning' ? 'Cleaning Staff Allocation' : activeTab === 'security' ? 'Security Staff Allocation' : 'Schedule Overview';

  // Stats for roster tab
  const totalStaff = staff.length;
  const presentCount = staff.filter((s) => s.is_active).length;

  return (
    <AppLayout title="Staff Allocation" breadcrumbs="Staff">
      {/* Sub Navigation */}
      <div
        className="flex overflow-x-auto -mx-8 -mt-8 mb-0"
        style={{
          background: 'white',
          borderBottom: '1px solid #E5E5E0',
          padding: '0 40px',
          gap: '32px',
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="whitespace-nowrap bg-transparent border-0 cursor-pointer transition-all"
            style={{
              padding: '16px 4px',
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: "'Lexend', sans-serif",
              color: activeTab === tab.key ? '#043959' : '#666666',
              borderBottom: `3px solid ${activeTab === tab.key ? '#043959' : 'transparent'}`,
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.key) {
                e.currentTarget.style.color = '#043959';
                e.currentTarget.style.borderBottomColor = '#F4A460';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.key) {
                e.currentTarget.style.color = '#666666';
                e.currentTarget.style.borderBottomColor = 'transparent';
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '32px 0' }}>
        {/* Header */}
        {activeTab !== 'roster' && (
          <div className="flex justify-between items-center" style={{ marginBottom: '20px' }}>
            <h3 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>{tabTitle}</h3>
            <button
              onClick={() => {
                resetForm();
                setForm((prev) => ({ ...prev, staff_type: activeTab === 'security' ? 'security' : 'cleaning' }));
                setShowModal(true);
              }}
              className="inline-flex items-center gap-2 text-white text-sm font-medium rounded-lg transition-all hover:opacity-90 border-0 cursor-pointer"
              style={{ padding: '10px 20px', background: '#043959', fontFamily: "'Lexend', sans-serif" }}
            >
              <span>+</span> Add Staff Member
            </button>
          </div>
        )}

        {activeTab === 'roster' && (
          <>
            <div className="flex justify-between items-center" style={{ marginBottom: '20px' }}>
              <h3 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>
                Staff Roster - {rosterFilters.date ? new Date(rosterFilters.date + 'T00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Today'}
              </h3>
            </div>

            {/* Filter Bar */}
            <div
              className="bg-white"
              style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '20px', marginBottom: '24px' }}
            >
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" style={{ marginBottom: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Service Type</label>
                  <select
                    value={rosterFilters.service_type}
                    onChange={(e) => handleFilterChange('service_type', e.target.value)}
                    className="w-full"
                    style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                  >
                    <option value="">All Types</option>
                    <option value="cleaning">Cleaning Staff</option>
                    <option value="security">Security Staff</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Date</label>
                  <input
                    type="date"
                    value={rosterFilters.date}
                    onChange={(e) => handleFilterChange('date', e.target.value)}
                    className="w-full"
                    style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Status</label>
                  <select
                    value={rosterFilters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full"
                    style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                  >
                    {statusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Tower</label>
                  <select
                    value={rosterFilters.tower}
                    onChange={(e) => handleFilterChange('tower', e.target.value)}
                    className="w-full"
                    style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                  >
                    <option value="">All Towers</option>
                    <option value="Tower A">Tower A</option>
                    <option value="Tower B">Tower B</option>
                    <option value="Parking">Parking</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Floor</label>
                  <select
                    value={rosterFilters.floor}
                    onChange={(e) => handleFilterChange('floor', e.target.value)}
                    className="w-full"
                    style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                    disabled={!rosterFilters.tower}
                  >
                    <option value="">All Floors</option>
                    {getRosterFloorOptions().map((f) => (
                      <option key={f} value={f}>Floor {f}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Shift</label>
                  <select
                    value={rosterFilters.shift}
                    onChange={(e) => handleFilterChange('shift', e.target.value)}
                    className="w-full"
                    style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                  >
                    {shiftOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={clearFilters}
                  className="text-sm font-medium rounded-lg cursor-pointer"
                  style={{ padding: '8px 16px', background: '#FAFAF8', color: '#666666', border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif" }}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </>
        )}

        {loading ? (
          <div className="text-center text-sm" style={{ padding: '60px 16px', color: '#999999' }}>
            Loading staff...
          </div>
        ) : staff.length === 0 ? (
          <div className="text-center text-sm" style={{ padding: '60px 16px', color: '#999999' }}>
            No staff found
          </div>
        ) : activeTab === 'roster' ? (
          /* Schedule Overview — Roster Table */
          <>
            <div
              className="bg-white overflow-hidden"
              style={{ borderRadius: '16px', border: '1px solid #E5E5E0', marginBottom: '24px' }}
            >
              <div className="overflow-x-auto">
                <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                      {['Staff Name', 'Service Type', 'Position/Area', 'Shift Time', 'Status', 'Contact'].map((h) => (
                        <th key={h} className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map((member) => {
                      const summary = getAssignmentSummary(member);
                      let positionText = '-';
                      if (summary) {
                        if (summary.posts) {
                          positionText = summary.posts.join(', ');
                        } else {
                          const parts = [];
                          if (summary.towers && Object.keys(summary.towers).length > 0) {
                            parts.push(Object.entries(summary.towers)
                              .map(([tower, floors]) => `${tower} (Floors ${floors.join(', ')})`)
                              .join(', '));
                          }
                          if (summary.sections && summary.sections.length > 0) {
                            parts.push(summary.sections.join(', '));
                          }
                          if (parts.length > 0) {
                            positionText = parts.join(' | ');
                          }
                        }
                      }
                      return (
                        <tr key={member.id} style={{ borderBottom: '1px solid #F0F0EB' }}>
                          <td style={{ padding: '16px' }}>
                            <div className="flex items-center gap-3">
                              <div
                                className="flex items-center justify-center text-white font-semibold"
                                style={{
                                  width: '32px', height: '32px', borderRadius: '50%', fontSize: '12px',
                                  background: 'linear-gradient(135deg, #043959, #F4A460)',
                                }}
                              >
                                {getInitials(member.name)}
                              </div>
                              <strong style={{ fontSize: '14px' }}>{member.name}</strong>
                            </div>
                          </td>
                          <td style={{ padding: '16px', fontSize: '14px' }}>
                            {member.staff_type === 'security' ? '🔐 Security' : '🧹 Cleaning'}
                          </td>
                          <td style={{ padding: '16px', fontSize: '14px' }}>{positionText}</td>
                          <td style={{ padding: '16px', fontSize: '14px' }}>
                            {formatTime12(member.work_start_time)} - {formatTime12(member.work_end_time)}
                          </td>
                          <td style={{ padding: '16px' }}>
                            <span
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 500,
                                background: member.is_active ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                                color: member.is_active ? '#10B981' : '#F59E0B',
                              }}
                            >
                              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />
                              {member.is_active ? 'Present' : 'Absent'}
                            </span>
                          </td>
                          <td style={{ padding: '16px', fontSize: '14px' }}>{member.phone || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Quick Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4" style={{ gap: '16px' }}>
              <div className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-2xl font-bold" style={{ color: '#1A1A1A' }}>{presentCount}</div>
                    <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>Present Today</div>
                  </div>
                  <div className="flex items-center justify-center" style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, rgba(16,185,129,0.1), rgba(16,185,129,0.2))', color: '#10B981', fontSize: '16px' }}>
                    ✓
                  </div>
                </div>
                <div className="text-xs" style={{ color: '#10B981', marginTop: '8px' }}>Out of {totalStaff} total staff</div>
              </div>

              <div className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-2xl font-bold" style={{ color: '#1A1A1A' }}>{totalStaff - presentCount}</div>
                    <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>On Leave / Absent</div>
                  </div>
                  <div className="flex items-center justify-center" style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(245,158,11,0.2))', color: '#F59E0B', fontSize: '16px' }}>
                    📅
                  </div>
                </div>
                <div className="text-xs" style={{ color: '#666666', marginTop: '8px' }}>Requires attention</div>
              </div>

              <div className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-2xl font-bold" style={{ color: '#1A1A1A' }}>{staff.filter((s) => s.staff_type === 'security').length}</div>
                    <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>Security Staff</div>
                  </div>
                  <div className="flex items-center justify-center" style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, rgba(59,130,246,0.1), rgba(59,130,246,0.2))', color: '#3B82F6', fontSize: '16px' }}>
                    🔐
                  </div>
                </div>
                <div className="text-xs" style={{ color: '#666666', marginTop: '8px' }}>Active personnel</div>
              </div>

              <div className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-2xl font-bold" style={{ color: '#1A1A1A' }}>{totalStaff}</div>
                    <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>Total Staff</div>
                  </div>
                  <div className="flex items-center justify-center" style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, rgba(4,57,89,0.1), rgba(4,57,89,0.2))', color: '#043959', fontSize: '16px' }}>
                    👥
                  </div>
                </div>
                <div className="text-xs" style={{ color: '#666666', marginTop: '8px' }}>Active personnel</div>
              </div>
            </div>
          </>
        ) : (
          /* Cleaning / Security — Staff Cards Grid */
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {staff.map((member) => {
              const summary = getAssignmentSummary(member);
              return (
                <div key={member.id} className="bg-white" style={{ border: '1px solid #E5E5E0', borderRadius: '12px', padding: '20px' }}>
                  {/* Staff Header */}
                  <div className="flex items-center gap-4" style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #F0F0EB' }}>
                    <div
                      className="flex items-center justify-center text-white font-semibold"
                      style={{
                        width: '56px', height: '56px', borderRadius: '50%', fontSize: '20px',
                        background: 'linear-gradient(135deg, #043959, #F4A460)',
                      }}
                    >
                      {getInitials(member.name)}
                    </div>
                    <div>
                      <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px', color: '#1A1A1A' }}>{member.name}</h4>
                      <p style={{ fontSize: '13px', color: '#999999' }}>
                        {member.staff_type === 'cleaning' ? 'Cleaning Staff' : 'Security Staff'}
                      </p>
                    </div>
                  </div>

                  {/* Assignments */}
                  {member.staff_type === 'cleaning' && summary?.towers && (
                    <>
                      <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Tower Assignment</div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                          {Object.keys(summary.towers).join(', ') || '-'}
                        </div>
                      </div>
                      <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Floor Assignment</div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                          {Object.entries(summary.towers).map(([, floors]) => `Floors ${floors.join(', ')}`).join(' | ') || '-'}
                        </div>
                      </div>
                    </>
                  )}

                  {member.staff_type === 'cleaning' && summary?.sections?.length > 0 && (
                    <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Cleaning Sections</div>
                      <div className="flex flex-wrap gap-1" style={{ marginTop: '4px' }}>
                        {summary.sections.map((section) => (
                          <span
                            key={section}
                            className="inline-flex items-center"
                            style={{
                              padding: '4px 8px', borderRadius: '6px',
                              fontSize: '11px', fontWeight: 600,
                              background: '#043959', color: 'white',
                            }}
                          >
                            {section}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {member.staff_type === 'security' && summary?.posts && (
                    <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Security Post</div>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                        {summary.posts.join(', ') || '-'}
                      </div>
                    </div>
                  )}

                  {/* Working Days */}
                  <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Working Days</div>
                    <div className="flex flex-wrap gap-1" style={{ marginTop: '4px' }}>
                      {allDays.map((day) => {
                        const isActive = member.working_days?.includes(day);
                        return (
                          <span
                            key={day}
                            className="inline-flex items-center justify-center"
                            style={{
                              minWidth: '36px', padding: '4px 8px', borderRadius: '6px',
                              fontSize: '11px', fontWeight: 600,
                              background: isActive ? '#043959' : 'white',
                              color: isActive ? 'white' : '#999999',
                              border: isActive ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                            }}
                          >
                            {dayLabels[day]}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Working Hours */}
                  <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Working Hours</div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                      {formatTime12(member.work_start_time)} - {formatTime12(member.work_end_time)}
                    </div>
                  </div>

                  {/* Contact */}
                  <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '0' }}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Contact</div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                      {member.phone || '-'}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2" style={{ marginTop: '16px' }}>
                    <button
                      onClick={() => handleDelete(member.id)}
                      className="text-sm font-medium rounded-lg transition-all cursor-pointer"
                      style={{
                        flex: 1, padding: '8px 12px',
                        background: '#FAFAF8', color: '#1A1A1A',
                        border: '1px solid #E5E5E0',
                        fontFamily: "'Lexend', sans-serif",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#EF4444';
                        e.currentTarget.style.color = '#EF4444';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#E5E5E0';
                        e.currentTarget.style.color = '#1A1A1A';
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {meta.last_page > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: meta.last_page }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => fetchStaff(page)}
                className="text-sm font-medium rounded-lg transition-all cursor-pointer"
                style={{
                  padding: '8px 14px',
                  background: page === meta.current_page ? '#043959' : 'white',
                  color: page === meta.current_page ? 'white' : '#1A1A1A',
                  border: `1px solid ${page === meta.current_page ? '#043959' : '#E5E5E0'}`,
                  fontFamily: "'Lexend', sans-serif",
                }}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Add Staff Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add Staff Member" size="3xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput label="Staff Name *" name="name" value={form.name} onChange={handleChange} placeholder="Enter staff name" required />
          <FormInput label="Phone Number" name="phone" value={form.phone} onChange={handleChange} placeholder="+91 xxxxx xxxxx" />

          {/* Staff Type */}
          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Staff Type *</label>
            <select
              name="staff_type"
              value={form.staff_type}
              onChange={(e) => setForm({ ...form, staff_type: e.target.value, towers: [], floorsA: [], floorsB: [], securityPosts: [], cleaningSections: [] })}
              className="w-full cursor-pointer"
              style={{
                padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                fontSize: '14px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none',
              }}
            >
              <option value="">Select Type</option>
              <option value="cleaning">Cleaning Staff</option>
              <option value="security">Security Staff</option>
            </select>
          </div>

          {/* Cleaning Assignment */}
          {form.staff_type === 'cleaning' && (
            <>
              <div>
                <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Area *</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginTop: '8px' }}>
                  {['Tower A', 'Tower B', 'Parking', 'Others'].map((tower) => (
                    <label
                      key={tower}
                      className="flex items-center gap-2 cursor-pointer"
                      style={{
                        padding: '10px 12px', borderRadius: '8px',
                        border: form.towers.includes(tower) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                        background: form.towers.includes(tower) ? 'rgba(4,57,89,0.05)' : 'white',
                        transition: 'all 0.2s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.towers.includes(tower)}
                        onChange={() => toggleArrayItem('towers', tower)}
                        style={{ width: '18px', height: '18px', accentColor: '#043959', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: 500, userSelect: 'none' }}>
                        {tower === 'Parking' ? 'Parking Floors' : tower}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '4px' }}>
                  {form.towers.length === 1 && form.towers.includes('Others') ? 'Cleaning Sections' : 'Floor Assignment *'}
                </label>
                <p style={{ fontSize: '12px', color: '#999999', marginBottom: '8px' }}>
                  {form.towers.length === 1 && form.towers.includes('Others') ? 'Select the cleaning task areas for this staff member' : 'Select individual floors for this staff member'}
                </p>

                {form.towers.includes('Tower A') && (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#043959' }}>Tower A Floors</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                      {towerAFloors.map((floor) => (
                        <label
                          key={`a-${floor}`}
                          className="flex items-center gap-2 cursor-pointer"
                          style={{
                            padding: '10px 12px', borderRadius: '8px',
                            border: form.floorsA.includes(floor) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                            background: form.floorsA.includes(floor) ? 'rgba(4,57,89,0.05)' : 'white',
                            transition: 'all 0.2s',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={form.floorsA.includes(floor)}
                            onChange={() => toggleArrayItem('floorsA', floor)}
                            style={{ width: '18px', height: '18px', accentColor: '#043959', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', fontWeight: 500, userSelect: 'none' }}>Floor {floor}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {form.towers.includes('Tower B') && (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#043959' }}>Tower B Floors</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                      {towerBFloors.map((floor) => (
                        <label
                          key={`b-${floor}`}
                          className="flex items-center gap-2 cursor-pointer"
                          style={{
                            padding: '10px 12px', borderRadius: '8px',
                            border: form.floorsB.includes(floor) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                            background: form.floorsB.includes(floor) ? 'rgba(4,57,89,0.05)' : 'white',
                            transition: 'all 0.2s',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={form.floorsB.includes(floor)}
                            onChange={() => toggleArrayItem('floorsB', floor)}
                            style={{ width: '18px', height: '18px', accentColor: '#043959', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', fontWeight: 500, userSelect: 'none' }}>Floor {floor}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {form.towers.includes('Others') && (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#043959' }}>Others</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
                      {generalCleaningSections.map((section) => (
                        <label
                          key={section}
                          className="flex items-center gap-2 cursor-pointer"
                          style={{
                            padding: '10px 12px', borderRadius: '8px',
                            border: form.cleaningSections.includes(section) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                            background: form.cleaningSections.includes(section) ? 'rgba(4,57,89,0.05)' : 'white',
                            transition: 'all 0.2s',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={form.cleaningSections.includes(section)}
                            onChange={() => toggleArrayItem('cleaningSections', section)}
                            style={{ width: '18px', height: '18px', accentColor: '#043959', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', fontWeight: 500, userSelect: 'none' }}>{section}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {(form.towers.includes('Tower A') || form.towers.includes('Tower B') || form.towers.includes('Parking')) && (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#043959' }}>Cleaning Sections</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '12px' }}>
                      {(() => {
                        const hasParking = form.towers.includes('Parking');
                        const hasTower = form.towers.includes('Tower A') || form.towers.includes('Tower B');
                        if (hasParking && !hasTower) return parkingCleaningSections;
                        if (!hasParking && hasTower) return towerCleaningSections;
                        if (hasParking && hasTower) return [...towerCleaningSections, ...parkingCleaningSections];
                        return [];
                      })().map((section) => (
                        <label
                          key={section}
                          className="flex items-center gap-2 cursor-pointer"
                          style={{
                            padding: '10px 12px', borderRadius: '8px',
                            border: form.cleaningSections.includes(section) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                            background: form.cleaningSections.includes(section) ? 'rgba(4,57,89,0.05)' : 'white',
                            transition: 'all 0.2s',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={form.cleaningSections.includes(section)}
                            onChange={() => toggleArrayItem('cleaningSections', section)}
                            style={{ width: '18px', height: '18px', accentColor: '#043959', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', fontWeight: 500, userSelect: 'none' }}>{section}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Security Assignment */}
          {form.staff_type === 'security' && (
            <>
              <div>
                <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '4px' }}>Security Post Assignment *</label>
                <p style={{ fontSize: '12px', color: '#999999', marginBottom: '8px' }}>Select the security post(s) for this staff member</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                  {securityPosts.map((post) => (
                    <label
                      key={post}
                      className="flex items-center gap-2 cursor-pointer"
                      style={{
                        padding: '10px 12px', borderRadius: '8px',
                        border: form.securityPosts.includes(post) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                        background: form.securityPosts.includes(post) ? 'rgba(4,57,89,0.05)' : 'white',
                        transition: 'all 0.2s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.securityPosts.includes(post)}
                        onChange={() => toggleArrayItem('securityPosts', post)}
                        style={{ width: '18px', height: '18px', accentColor: '#043959', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: 500, userSelect: 'none' }}>{post}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Shift Type</label>
                <select
                  value={`${form.work_start_time}-${form.work_end_time}`}
                  onChange={(e) => {
                    const [start, end] = e.target.value.split('-');
                    setForm({ ...form, work_start_time: start, work_end_time: end });
                  }}
                  className="w-full cursor-pointer"
                  style={{
                    padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                    fontSize: '14px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none',
                  }}
                >
                  <option value="">Select Shift</option>
                  <option value="08:00-16:00">Day Shift (8:00 AM - 4:00 PM)</option>
                  <option value="16:00-00:00">Evening Shift (4:00 PM - 12:00 AM)</option>
                  <option value="00:00-08:00">Night Shift (12:00 AM - 8:00 AM)</option>
                </select>
              </div>
            </>
          )}

          {/* Working Days */}
          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Working Days *</label>
            <div className="flex flex-wrap gap-2" style={{ marginTop: '8px' }}>
              {allDays.map((day) => (
                <div
                  key={day}
                  onClick={() => toggleDay(day)}
                  className="cursor-pointer inline-flex items-center justify-center"
                  style={{
                    minWidth: '36px', padding: '4px 8px', borderRadius: '6px',
                    fontSize: '11px', fontWeight: 600, userSelect: 'none',
                    background: form.working_days.includes(day) ? '#043959' : 'white',
                    color: form.working_days.includes(day) ? 'white' : '#999999',
                    border: form.working_days.includes(day) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                    transition: 'all 0.2s',
                  }}
                >
                  {dayLabels[day]}
                </div>
              ))}
            </div>
          </div>

          {/* Working Hours (for cleaning or custom) */}
          {form.staff_type === 'cleaning' && (
            <div>
              <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Working Hours *</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label style={{ fontSize: '12px', color: '#999999', marginBottom: '4px', display: 'block' }}>Start Time</label>
                  <input
                    type="time"
                    name="work_start_time"
                    value={form.work_start_time}
                    onChange={handleChange}
                    className="w-full"
                    style={{
                      padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                      fontSize: '14px', fontFamily: "'Lexend', sans-serif", outline: 'none',
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#999999', marginBottom: '4px', display: 'block' }}>End Time</label>
                  <input
                    type="time"
                    name="work_end_time"
                    value={form.work_end_time}
                    onChange={handleChange}
                    className="w-full"
                    style={{
                      padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                      fontSize: '14px', fontFamily: "'Lexend', sans-serif", outline: 'none',
                    }}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid #E5E5E0' }}>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="text-sm font-medium rounded-lg transition-all cursor-pointer"
              style={{
                padding: '10px 20px', background: '#FAFAF8', color: '#1A1A1A',
                border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="text-sm font-medium rounded-lg text-white transition-all cursor-pointer border-0"
              style={{
                padding: '10px 20px', background: '#043959',
                fontFamily: "'Lexend', sans-serif", opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? 'Adding...' : 'Add Staff Member'}
            </button>
          </div>
        </form>
      </Modal>
    </AppLayout>
  );
}
